import React from 'react';
import { X, Keyboard } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

interface Shortcut {
  keys: string[];
  description: string;
  category: string;
}

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
  shortcuts?: Shortcut[];
}

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
  shortcuts: customShortcuts,
}) => {
  const { t } = useI18n();

  // Atalhos padrão do sistema
  const defaultShortcuts: Shortcut[] = [
    {
      keys: ['Ctrl', 'C'],
      description: 'Criar novo item',
      category: 'Ações Gerais',
    },
    {
      keys: ['Ctrl', 'S'],
      description: 'Salvar formulário',
      category: 'Ações Gerais',
    },
    {
      keys: ['Esc'],
      description: 'Cancelar/Fechar modal',
      category: 'Ações Gerais',
    },
    {
      keys: ['Ctrl', 'F'],
      description: 'Buscar/Filtrar',
      category: 'Ações Gerais',
    },
    {
      keys: ['Ctrl', 'K'],
      description: 'Abrir busca global',
      category: 'Navegação',
    },
    {
      keys: ['Ctrl', '/'],
      description: 'Mostrar atalhos de teclado',
      category: 'Navegação',
    },
    {
      keys: ['Ctrl', 'H'],
      description: 'Ir para Dashboard',
      category: 'Navegação',
    },
  ];

  const shortcuts = customShortcuts || defaultShortcuts;

  // Agrupar por categoria
  const shortcutsByCategory = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {} as Record<string, Shortcut[]>);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '30px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Keyboard size={24} style={{ color: '#6366f1' }} />
            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '24px', fontWeight: '600' }}>
              Atalhos de Teclado
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label={t('actions.close')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Shortcuts List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {Object.entries(shortcutsByCategory).map(([category, categoryShortcuts]) => (
            <div key={category}>
              <h3
                style={{
                  margin: '0 0 12px 0',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {category}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {categoryShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                    }}
                  >
                    <span style={{ color: '#4b5563', fontSize: '14px' }}>{shortcut.description}</span>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {shortcut.keys.map((key, keyIndex) => (
                        <React.Fragment key={keyIndex}>
                          <kbd
                            style={{
                              padding: '4px 8px',
                              backgroundColor: 'white',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: '#374151',
                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                              fontFamily: 'monospace',
                            }}
                          >
                            {key}
                          </kbd>
                          {keyIndex < shortcut.keys.length - 1 && (
                            <span style={{ color: '#9ca3af', fontSize: '12px' }}>+</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: '24px',
            paddingTop: '20px',
            borderTop: '1px solid #e5e7eb',
            textAlign: 'center',
          }}
        >
          <p style={{ margin: 0, color: '#6b7280', fontSize: '12px' }}>
            Pressione <kbd style={{ padding: '2px 6px', backgroundColor: '#f3f4f6', borderRadius: '3px', fontSize: '11px' }}>Esc</kbd> para fechar
          </p>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsModal;

